import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { t } from 'i18next';
import { useContext, useEffect, useMemo, useState } from 'react';

import Checkbox from '~components/Checkbox/Checkbox';
import useGroupBillingLinesQuery from '~queries/useGroupBillingLinesQuery';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import ApproveChargesPageContext from '../ApproveChargesPageContext';
import { ChargesGrainTab } from '../approveChargesTabs';
import { isBillingLineSelectable } from '../chargeSelection';
import { ChargesGroup } from '../chargesGrouping';
import { ChargesSelectionActionsContext } from '../ChargesSelectionContext';
import {
  getPerLoadLinesSettledByUnit,
  getTimeBasedLines,
  groupBillingLinesByJob,
} from '../groupBillingLines';
import ChargeLinesCheckbox from './ChargeLinesCheckbox';

const LOADING_INDICATOR_SIZE_PX = 18;

interface ChargesGroupCheckboxProps {
  group: ChargesGroup;
}

export default function ChargesGroupCheckbox({ group }: ChargesGroupCheckboxProps) {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { grainTab } = useContext(ApproveChargesPageContext);
  const { toggleLinesCheck } = useContext(ChargesSelectionActionsContext);
  // The lines are read on the first hover or click, so a page of groups doesn't read
  // every line of every job up front
  const [isReadRequested, setIsReadRequested] = useState(false);
  const [isTogglePending, setIsTogglePending] = useState(false);
  const { isError, isFetching, lines, refetch } = useGroupBillingLinesQuery(
    companyId,
    group.jobIds,
    isReadRequested,
  );
  const hasReadFailed = isError && !isFetching;

  const groupLines = useMemo(() => {
    if (!lines) {
      return null;
    }

    const getJobLines =
      grainTab === ChargesGrainTab.HOURLY_JOBS
        ? getTimeBasedLines
        : getPerLoadLinesSettledByUnit;

    return [...groupBillingLinesByJob(lines).values()].flatMap(getJobLines);
  }, [grainTab, lines]);

  useEffect(() => {
    if (isTogglePending && groupLines) {
      setIsTogglePending(false);
      toggleLinesCheck(groupLines.filter(isBillingLineSelectable));
    }
  }, [groupLines, isTogglePending, toggleLinesCheck]);

  useEffect(() => {
    if (hasReadFailed) {
      setIsTogglePending(false);
    }
  }, [hasReadFailed]);

  const selectLinesLabel = t('approvals.approve_charges.selection.select_lines', {
    interpolation: { escapeValue: false },
    name: group.name,
  });

  if (isFetching && !groupLines) {
    return (
      <Box
        aria-label={selectLinesLabel}
        role="progressbar"
        sx={{ display: 'inline-flex', p: 1 }}
      >
        <CircularProgress size={LOADING_INDICATOR_SIZE_PX} />
      </Box>
    );
  }

  if (groupLines) {
    return <ChargeLinesCheckbox label={group.name} lines={groupLines} />;
  }

  return (
    <Box
      component="span"
      onMouseEnter={() => setIsReadRequested(true)}
      sx={{ display: 'inline-flex' }}
    >
      <Checkbox
        ariaLabel={selectLinesLabel}
        isChecked={false}
        onChange={() => {
          setIsReadRequested(true);
          setIsTogglePending(true);

          if (hasReadFailed) {
            void refetch();
          }
        }}
      />
    </Box>
  );
}
